import { useState, useEffect, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import axios from "axios";
import {
  AlertTriangle, ShieldCheck, RefreshCw, Hammer, MapPin, Phone,
  User, Clock, CheckCircle2, XCircle, Scale, ArrowDown, Wallet,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STATUS_PILLS = [
  { id: "open", label: "Ouverts" },
  { id: "resolved", label: "Résolus" },
  { id: "all", label: "Tous" },
];

function formatFCFA(n) {
  return Number(n || 0).toLocaleString("fr-FR") + " FCFA";
}

function formatDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("fr-FR", {
      day: "2-digit", month: "2-digit", year: "2-digit", hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

// ============================
//  DELIVERY DISPUTE CARD
// ============================
function DeliveryDisputeCard({ dispute, onResolved }) {
  const [open, setOpen] = useState(false);
  const [resolution, setResolution] = useState("refunded");
  const [partialAmount, setPartialAmount] = useState("");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);

  const isResolved = dispute.status === "resolved";
  const orderFee = dispute.order?.delivery_fee || dispute.order?.total_amount || 0;

  const submit = async () => {
    setBusy(true);
    try {
      const body = { resolution, admin_notes: notes };
      if (resolution === "partial_refund") body.refund_amount = parseInt(partialAmount || "0", 10);
      const r = await axios.patch(`${API}/admin/disputes/${dispute.id}/resolve`, body);
      toast.success(`Litige résolu (remboursé : ${formatFCFA(r.data.refund_amount)})`);
      setOpen(false);
      onResolved?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors de la résolution");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="rounded-3xl border border-[#D4AF37]/15 bg-[#1E293B] p-5 hover:border-[#D4AF37]/40 transition-colors"
      data-testid={`dispute-card-${dispute.id}`}
    >
      {/* Header */}
      <div className="flex items-start justify-between gap-3 flex-wrap mb-4">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-red-500/15 text-red-400 flex items-center justify-center flex-shrink-0">
            <AlertTriangle className="w-5 h-5" />
          </div>
          <div>
            <p className="text-white font-black uppercase tracking-widest text-xs">
              #{dispute.id.substring(0, 8)} · {dispute.reason_label}
            </p>
            <p className="text-[10px] text-slate-400 uppercase tracking-widest mt-0.5">
              Commande #{(dispute.order_id || "").substring(0, 8)} · {formatDate(dispute.created_at)}
            </p>
          </div>
        </div>
        <span
          className={`px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest ${
            isResolved
              ? "bg-emerald-500/15 text-emerald-300 border border-emerald-500/30"
              : "bg-red-500/15 text-red-300 border border-red-500/30"
          }`}
        >
          {isResolved ? `RÉSOLU · ${dispute.resolution || ""}` : "EN ATTENTE"}
        </span>
      </div>

      {/* Body grid */}
      <div className="grid md:grid-cols-2 gap-4 mb-4">
        <div className="bg-[#0F172A] rounded-2xl p-3 border border-white/5">
          <p className="text-[9px] text-slate-500 font-bold uppercase tracking-widest mb-1">Client</p>
          <div className="flex items-center gap-2 text-sm text-white">
            <User className="w-4 h-4 text-[#D4AF37]" />
            <span className="font-semibold">{dispute.customer_name || "—"}</span>
          </div>
          {dispute.customer_phone && (
            <a href={`tel:${dispute.customer_phone}`} className="flex items-center gap-2 text-xs text-slate-400 mt-1 hover:text-[#D4AF37]">
              <Phone className="w-3 h-3" /> {dispute.customer_phone}
            </a>
          )}
        </div>
        <div className="bg-[#0F172A] rounded-2xl p-3 border border-white/5">
          <p className="text-[9px] text-slate-500 font-bold uppercase tracking-widest mb-1">Livreur</p>
          <div className="flex items-center gap-2 text-sm text-white">
            <User className="w-4 h-4 text-[#003594]" />
            <span className="font-semibold">{dispute.courier_name || "Non assigné"}</span>
          </div>
          {dispute.courier_phone && (
            <a href={`tel:${dispute.courier_phone}`} className="flex items-center gap-2 text-xs text-slate-400 mt-1 hover:text-[#D4AF37]">
              <Phone className="w-3 h-3" /> {dispute.courier_phone}
            </a>
          )}
        </div>
      </div>

      {dispute.comment && (
        <div className="bg-[#0F172A] rounded-2xl p-3 mb-4 border border-white/5">
          <p className="text-[9px] text-slate-500 font-bold uppercase tracking-widest mb-1">Commentaire client</p>
          <p className="text-sm text-slate-200 italic">« {dispute.comment} »</p>
        </div>
      )}

      {dispute.order && (
        <div className="bg-[#0F172A] rounded-2xl p-3 mb-4 border border-white/5 text-xs space-y-2">
          {dispute.order.pickup_address && (
            <div className="flex items-start gap-2 text-slate-300">
              <MapPin className="w-3.5 h-3.5 text-[#003594] mt-0.5" />
              <span><strong className="text-slate-400 uppercase text-[9px] tracking-widest">Retrait:</strong> {dispute.order.pickup_address}</span>
            </div>
          )}
          {dispute.order.delivery_address && (
            <div className="flex items-start gap-2 text-slate-300">
              <MapPin className="w-3.5 h-3.5 text-[#D4AF37] mt-0.5" />
              <span><strong className="text-slate-400 uppercase text-[9px] tracking-widest">Livraison:</strong> {dispute.order.delivery_address}</span>
            </div>
          )}
          <div className="flex items-center gap-2 text-slate-300">
            <Wallet className="w-3.5 h-3.5 text-[#D4AF37]" />
            <span className="font-bold text-[#D4AF37]">{formatFCFA(orderFee)}</span>
          </div>
        </div>
      )}

      {isResolved ? (
        <div className="bg-emerald-500/5 rounded-2xl p-3 border border-emerald-500/20 text-xs">
          <p className="text-emerald-300 font-black uppercase tracking-widest text-[10px] mb-1">
            Résolu le {formatDate(dispute.resolved_at)} — {dispute.resolution}
          </p>
          {dispute.refund_amount > 0 && (
            <p className="text-emerald-200">Remboursement client : {formatFCFA(dispute.refund_amount)}</p>
          )}
          {dispute.admin_notes && <p className="text-slate-300 italic mt-1">« {dispute.admin_notes} »</p>}
        </div>
      ) : !open ? (
        <Button
          onClick={() => setOpen(true)}
          className="w-full bg-gradient-to-r from-[#D4AF37] to-[#B8962E] hover:opacity-90 text-[#0F172A] rounded-full font-black uppercase tracking-widest text-xs"
          data-testid={`resolve-delivery-${dispute.id}`}
        >
          <Scale className="w-4 h-4 mr-2" /> Trancher le litige
        </Button>
      ) : (
        <div className="bg-[#0F172A] rounded-2xl p-4 border border-[#D4AF37]/30 space-y-3">
          <div>
            <p className="text-[10px] text-slate-400 font-bold uppercase tracking-widest mb-2">Décision</p>
            <div className="grid grid-cols-2 gap-2">
              {[
                { id: "refunded", label: "Remboursement total", icon: RefreshCw },
                { id: "partial_refund", label: "Remboursement partiel", icon: ArrowDown },
                { id: "courier_warned", label: "Avertir le livreur", icon: AlertTriangle },
                { id: "dismissed", label: "Rejeter (sans suite)", icon: XCircle },
              ].map((opt) => {
                const Icon = opt.icon;
                const active = resolution === opt.id;
                return (
                  <button
                    key={opt.id}
                    onClick={() => setResolution(opt.id)}
                    className={`flex items-center gap-2 p-2.5 rounded-xl text-[10px] font-black uppercase tracking-widest border transition-all ${
                      active
                        ? "bg-[#D4AF37]/15 text-[#D4AF37] border-[#D4AF37]"
                        : "bg-white/5 text-slate-300 border-white/10 hover:border-white/30"
                    }`}
                    data-testid={`resolution-${opt.id}-${dispute.id}`}
                  >
                    <Icon className="w-3.5 h-3.5" /> {opt.label}
                  </button>
                );
              })}
            </div>
          </div>
          {resolution === "partial_refund" && (
            <div>
              <p className="text-[10px] text-slate-400 font-bold uppercase tracking-widest mb-1">Montant à rembourser (FCFA)</p>
              <Input
                type="number"
                min="0"
                value={partialAmount}
                onChange={(e) => setPartialAmount(e.target.value)}
                placeholder={`Max ${formatFCFA(orderFee)}`}
                className="bg-[#1E293B] border-white/10 text-white"
                data-testid={`partial-amount-${dispute.id}`}
              />
            </div>
          )}
          <div>
            <p className="text-[10px] text-slate-400 font-bold uppercase tracking-widest mb-1">Notes admin (optionnel)</p>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              className="bg-[#1E293B] border-white/10 text-white text-sm"
              placeholder="Contexte de la décision, mesures prises..."
              data-testid={`notes-${dispute.id}`}
            />
          </div>
          <div className="flex gap-2">
            <Button
              onClick={submit}
              disabled={busy}
              className="flex-1 bg-gradient-to-r from-[#D4AF37] to-[#B8962E] hover:opacity-90 text-[#0F172A] rounded-full font-black uppercase tracking-widest text-xs"
              data-testid={`submit-resolve-${dispute.id}`}
            >
              <CheckCircle2 className="w-4 h-4 mr-2" /> {busy ? "..." : "Confirmer"}
            </Button>
            <Button
              onClick={() => setOpen(false)}
              variant="outline"
              className="rounded-full border-white/20 text-slate-300 hover:bg-white/5 text-xs"
            >
              Annuler
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

// ============================
//  MEDIATION CASE CARD (artisan)
// ============================
function MediationCard({ kase, onResolved }) {
  const [open, setOpen] = useState(false);
  const [decision, setDecision] = useState("split");
  const [pct, setPct] = useState(50);
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);

  const isResolved = kase.status === "resolved";
  const escrow = kase.project?.escrow_amount || 0;
  const fee = kase.project?.platform_fee || 0;

  const submit = async () => {
    setBusy(true);
    try {
      const body = {
        case_id: kase.id,
        decision,
        artisan_share_pct: decision === "split" ? pct : 50,
        admin_notes: notes,
      };
      const r = await axios.post(`${API}/admin/mediation/resolve`, body);
      toast.success(
        `Médiation résolue · Artisan +${formatFCFA(r.data.artisan_share)} · Client +${formatFCFA(r.data.client_refund)}`
      );
      setOpen(false);
      onResolved?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors de la résolution");
    } finally {
      setBusy(false);
    }
  };

  // preview shares
  const preview = (() => {
    if (decision === "release_to_artisan") return { artisan: escrow - fee, client: 0 };
    if (decision === "refund_to_client") return { artisan: 0, client: escrow };
    const a = Math.round((escrow - fee) * pct / 100);
    return { artisan: a, client: escrow - a - fee };
  })();

  return (
    <div
      className="rounded-3xl border border-[#003594]/30 bg-[#1E293B] p-5 hover:border-[#003594]/60 transition-colors"
      data-testid={`mediation-card-${kase.id}`}
    >
      <div className="flex items-start justify-between gap-3 flex-wrap mb-4">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-[#003594]/20 text-[#003594] flex items-center justify-center flex-shrink-0">
            <Hammer className="w-5 h-5" />
          </div>
          <div>
            <p className="text-white font-black uppercase tracking-widest text-xs">
              {kase.project?.title || "Projet artisan"}
            </p>
            <p className="text-[10px] text-slate-400 uppercase tracking-widest mt-0.5">
              {kase.project?.category} · {kase.reason} · {formatDate(kase.created_at)}
            </p>
          </div>
        </div>
        <span
          className={`px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest ${
            isResolved
              ? "bg-emerald-500/15 text-emerald-300 border border-emerald-500/30"
              : "bg-[#D4AF37]/15 text-[#D4AF37] border border-[#D4AF37]/30"
          }`}
        >
          {isResolved ? `RÉSOLU · ${kase.decision}` : "OUVERT"}
        </span>
      </div>

      <div className="grid md:grid-cols-3 gap-3 mb-4">
        <div className="bg-[#0F172A] rounded-2xl p-3 border border-white/5">
          <p className="text-[9px] text-slate-500 font-bold uppercase tracking-widest mb-1">Client</p>
          <p className="text-sm text-white font-semibold">{kase.client_name || "—"}</p>
          {kase.client_phone && <p className="text-[10px] text-slate-400 mt-1">{kase.client_phone}</p>}
        </div>
        <div className="bg-[#0F172A] rounded-2xl p-3 border border-white/5">
          <p className="text-[9px] text-slate-500 font-bold uppercase tracking-widest mb-1">Artisan</p>
          <p className="text-sm text-white font-semibold">{kase.artisan_name || "—"}</p>
          {kase.artisan_phone && <p className="text-[10px] text-slate-400 mt-1">{kase.artisan_phone}</p>}
        </div>
        <div className="bg-[#0F172A] rounded-2xl p-3 border border-[#D4AF37]/20">
          <p className="text-[9px] text-slate-500 font-bold uppercase tracking-widest mb-1">Escrow</p>
          <p className="text-sm text-[#D4AF37] font-black">{formatFCFA(escrow)}</p>
          <p className="text-[10px] text-slate-500 mt-1">Frais plateforme : {formatFCFA(fee)}</p>
        </div>
      </div>

      <div className="bg-[#0F172A] rounded-2xl p-3 mb-4 border border-white/5">
        <p className="text-[9px] text-slate-500 font-bold uppercase tracking-widest mb-1">
          Litige ouvert par {kase.raised_by === kase.client_id ? "le CLIENT" : "l'ARTISAN"}
        </p>
        <p className="text-sm text-slate-200 italic">« {kase.description || "—"} »</p>
      </div>

      {isResolved ? (
        <div className="bg-emerald-500/5 rounded-2xl p-3 border border-emerald-500/20 text-xs space-y-1">
          <p className="text-emerald-300 font-black uppercase tracking-widest text-[10px]">
            Résolu le {formatDate(kase.resolved_at)} — décision : {kase.decision}
          </p>
          <p className="text-emerald-200">Artisan : {formatFCFA(kase.artisan_share)} · Client : {formatFCFA(kase.client_refund)} · Plateforme : {formatFCFA(kase.platform_fee_kept)}</p>
          {kase.admin_notes && <p className="text-slate-300 italic mt-1">« {kase.admin_notes} »</p>}
        </div>
      ) : !open ? (
        <Button
          onClick={() => setOpen(true)}
          className="w-full bg-gradient-to-r from-[#003594] to-[#003380] hover:opacity-90 text-white rounded-full font-black uppercase tracking-widest text-xs"
          data-testid={`resolve-mediation-${kase.id}`}
        >
          <Scale className="w-4 h-4 mr-2" /> Médier ce conflit
        </Button>
      ) : (
        <div className="bg-[#0F172A] rounded-2xl p-4 border border-[#003594]/40 space-y-3">
          <div>
            <p className="text-[10px] text-slate-400 font-bold uppercase tracking-widest mb-2">Décision</p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
              {[
                { id: "release_to_artisan", label: "Libérer artisan (100%)", icon: ShieldCheck },
                { id: "refund_to_client", label: "Rembourser client (100%)", icon: RefreshCw },
                { id: "split", label: "Partage personnalisé", icon: Scale },
              ].map((opt) => {
                const Icon = opt.icon;
                const active = decision === opt.id;
                return (
                  <button
                    key={opt.id}
                    onClick={() => setDecision(opt.id)}
                    className={`flex items-center gap-2 p-2.5 rounded-xl text-[10px] font-black uppercase tracking-widest border transition-all ${
                      active
                        ? "bg-[#003594]/20 text-[#60A5FA] border-[#003594]"
                        : "bg-white/5 text-slate-300 border-white/10 hover:border-white/30"
                    }`}
                    data-testid={`decision-${opt.id}-${kase.id}`}
                  >
                    <Icon className="w-3.5 h-3.5" /> {opt.label}
                  </button>
                );
              })}
            </div>
          </div>

          {decision === "split" && (
            <div>
              <div className="flex justify-between mb-1">
                <p className="text-[10px] text-slate-400 font-bold uppercase tracking-widest">Part artisan</p>
                <p className="text-[10px] text-[#D4AF37] font-black">{pct}%</p>
              </div>
              <input
                type="range"
                min="0"
                max="100"
                value={pct}
                onChange={(e) => setPct(parseInt(e.target.value, 10))}
                className="w-full accent-[#D4AF37]"
                data-testid={`split-pct-${kase.id}`}
              />
              <div className="flex justify-between text-[9px] text-slate-500 mt-1 uppercase tracking-widest">
                <span>Client {100 - pct}%</span>
                <span>Artisan {pct}%</span>
              </div>
            </div>
          )}

          {/* Preview */}
          <div className="grid grid-cols-3 gap-2 text-center">
            <div className="bg-[#1E293B] rounded-xl p-2 border border-white/5">
              <p className="text-[9px] text-slate-500 uppercase tracking-widest">Artisan</p>
              <p className="text-sm font-black text-[#D4AF37]">{formatFCFA(preview.artisan)}</p>
            </div>
            <div className="bg-[#1E293B] rounded-xl p-2 border border-white/5">
              <p className="text-[9px] text-slate-500 uppercase tracking-widest">Client</p>
              <p className="text-sm font-black text-[#60A5FA]">{formatFCFA(preview.client)}</p>
            </div>
            <div className="bg-[#1E293B] rounded-xl p-2 border border-white/5">
              <p className="text-[9px] text-slate-500 uppercase tracking-widest">Plateforme</p>
              <p className="text-sm font-black text-emerald-400">{formatFCFA(fee)}</p>
            </div>
          </div>

          <div>
            <p className="text-[10px] text-slate-400 font-bold uppercase tracking-widest mb-1">Notes admin</p>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              className="bg-[#1E293B] border-white/10 text-white text-sm"
              placeholder="Justification de la médiation..."
              data-testid={`mediation-notes-${kase.id}`}
            />
          </div>

          <div className="flex gap-2">
            <Button
              onClick={submit}
              disabled={busy}
              className="flex-1 bg-gradient-to-r from-[#D4AF37] to-[#B8962E] hover:opacity-90 text-[#0F172A] rounded-full font-black uppercase tracking-widest text-xs"
              data-testid={`submit-mediation-${kase.id}`}
            >
              <CheckCircle2 className="w-4 h-4 mr-2" /> {busy ? "..." : "Confirmer la médiation"}
            </Button>
            <Button
              onClick={() => setOpen(false)}
              variant="outline"
              className="rounded-full border-white/20 text-slate-300 hover:bg-white/5 text-xs"
            >
              Annuler
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

// ============================
//  MAIN PAGE
// ============================
export default function AdminDisputes() {
  const [tab, setTab] = useState("delivery"); // delivery | mediation
  const [statusFilter, setStatusFilter] = useState("open");
  const [deliveryDisputes, setDeliveryDisputes] = useState([]);
  const [mediationCases, setMediationCases] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const q = statusFilter === "all" ? "" : `?status=${statusFilter}`;
      const [d, m] = await Promise.all([
        axios.get(`${API}/admin/disputes${q}`),
        axios.get(`${API}/admin/mediation${q}`),
      ]);
      setDeliveryDisputes(d.data || []);
      setMediationCases(m.data || []);
    } catch (err) {
      console.error("Disputes fetch error:", err);
      toast.error(err.response?.data?.detail || "Erreur de chargement des litiges");
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const list = tab === "delivery" ? deliveryDisputes : mediationCases;
  const openCount = {
    delivery: deliveryDisputes.filter((d) => d.status === "open").length,
    mediation: mediationCases.filter((c) => c.status === "open").length,
  };

  return (
    <div className="min-h-full bg-[#0F172A] text-white -m-6 p-6 rounded-2xl" data-testid="admin-disputes-page">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap mb-6">
        <div>
          <h2 className="text-2xl md:text-3xl font-black italic tracking-tight">
            <span className="text-[#D4AF37]">CELLULE</span> de médiation
          </h2>
          <p className="text-[10px] text-slate-400 uppercase tracking-widest mt-1">
            Litiges livraison · Escrow artisan
          </p>
        </div>
        <Button
          onClick={fetchAll}
          disabled={loading}
          className="rounded-full bg-white/5 hover:bg-white/10 text-slate-300 border border-white/10 text-xs"
          data-testid="disputes-refresh-btn"
        >
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`} /> Actualiser
        </Button>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-4 flex-wrap">
        <button
          onClick={() => setTab("delivery")}
          className={`flex items-center gap-2 px-4 py-2 rounded-full text-xs font-black uppercase tracking-widest border transition-all ${
            tab === "delivery"
              ? "bg-gradient-to-r from-[#D4AF37] to-[#B8962E] text-[#0F172A] border-transparent"
              : "bg-[#1E293B] text-slate-300 border-white/10 hover:border-white/30"
          }`}
          data-testid="tab-delivery"
        >
          <AlertTriangle className="w-3.5 h-3.5" />
          Livraisons ({openCount.delivery})
        </button>
        <button
          onClick={() => setTab("mediation")}
          className={`flex items-center gap-2 px-4 py-2 rounded-full text-xs font-black uppercase tracking-widest border transition-all ${
            tab === "mediation"
              ? "bg-gradient-to-r from-[#003594] to-[#003380] text-white border-transparent"
              : "bg-[#1E293B] text-slate-300 border-white/10 hover:border-white/30"
          }`}
          data-testid="tab-mediation"
        >
          <Hammer className="w-3.5 h-3.5" />
          Artisans ({openCount.mediation})
        </button>
      </div>

      {/* Status filter */}
      <div className="flex gap-2 mb-6 flex-wrap">
        {STATUS_PILLS.map((p) => (
          <button
            key={p.id}
            onClick={() => setStatusFilter(p.id)}
            className={`px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest border transition-all ${
              statusFilter === p.id
                ? "bg-white/10 text-white border-[#D4AF37]/60"
                : "bg-transparent text-slate-400 border-white/10 hover:border-white/30"
            }`}
            data-testid={`filter-${p.id}`}
          >
            {p.label}
          </button>
        ))}
      </div>

      {/* List */}
      {loading ? (
        <div className="text-center py-16 text-slate-400" data-testid="disputes-loading">
          <Clock className="w-8 h-8 mx-auto mb-3 animate-spin" />
          Chargement…
        </div>
      ) : list.length === 0 ? (
        <div className="text-center py-16 bg-[#1E293B] rounded-3xl border border-white/5" data-testid="disputes-empty">
          <CheckCircle2 className="w-12 h-12 mx-auto mb-3 text-emerald-400" />
          <p className="text-white font-black uppercase tracking-widest text-sm">Aucun litige</p>
          <p className="text-[10px] text-slate-400 mt-1 tracking-widest">
            {tab === "delivery" ? "Toutes les livraisons sont saines" : "Aucun conflit artisan en cours"}
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {tab === "delivery"
            ? list.map((d) => (
                <DeliveryDisputeCard key={d.id} dispute={d} onResolved={fetchAll} />
              ))
            : list.map((c) => (
                <MediationCard key={c.id} kase={c} onResolved={fetchAll} />
              ))}
        </div>
      )}
    </div>
  );
}
